import { canvas, drawQuad, drawTriangles, lineToNdcQuad, whiteTexture } from "./gl.js";
import { clamp } from "./utils.js";
import {
  clampBoxCenterNormalized,
  clampBoxesToPanel,
  getBoxById,
  getBoxCenterPixels,
  getBoxRectPixels,
  getCanvasPoint,
  getPanelRectForInput,
  pointInRect,
} from "./layout.js";
import {
  appState,
  editor,
  EDGE_HIT_DISTANCE,
  EDITOR_LINE_WIDTH,
  ARROW_LENGTH,
  ARROW_WIDTH,
  DEFAULT_BOX_SIZE,
  DRAG_THRESHOLD,
  gameState,
  moveState,
  selectionState,
  turnState,
  yutAnim,
  oneMoreOverlay,
  yutResultOverlay,
  yutThrowState,
} from "./state.js";
import { resetPiecesForStart } from "./pieces.js";
import { updateYutThrowUI } from "./yut.js";

const debugButton = document.getElementById("debug-toggle");

export function setEditorEnabled(enabled) {
  editor.enabled = enabled;
  if (debugButton) {
    debugButton.classList.toggle("is-active", enabled);
    debugButton.setAttribute("aria-pressed", enabled ? "true" : "false");
    debugButton.textContent = enabled ? "DEBUG ON" : "DEBUG";
  }
  appState.requestDraw?.();
}

export function renderEditorOverlay(panelRect) {
  // Editor visuals disabled for performance.
  return;
  clampBoxesToPanel(panelRect);
  const edgeColor = [0.6, 0.85, 1, 0.8];
  const edgeSelectedColor = [1, 0.8, 0.2, 0.9];
  const boxColor = [1, 1, 1, 0.9];
  const boxSelectedColor = [1, 0.8, 0.2, 1];
  const startColor = [0.3, 1, 0.6, 1];
  const previewColor = [1, 1, 1, 0.4];

  for (let i = 0; i < editor.edges.length; i += 1) {
    const edge = editor.edges[i];
    const fromBox = getBoxById(edge.from);
    const toBox = getBoxById(edge.to);
    if (!fromBox || !toBox) {
      continue;
    }
    const start = getBoxCenterPixels(fromBox, panelRect);
    const end = getBoxCenterPixels(toBox, panelRect);
    const color = i === editor.selectedEdgeId ? edgeSelectedColor : edgeColor;
    drawLinePixels(start, end, EDITOR_LINE_WIDTH, color);
    drawArrowHeadPixels(start, end, ARROW_LENGTH, ARROW_WIDTH, color);
  }

  if (editor.dragMode === "link" && editor.linkFromId && editor.linkPreviewPos) {
    const fromBox = getBoxById(editor.linkFromId);
    if (fromBox) {
      const start = getBoxCenterPixels(fromBox, panelRect);
      drawLinePixels(start, editor.linkPreviewPos, EDITOR_LINE_WIDTH, previewColor);
    }
  }

  for (let i = 0; i < editor.boxes.length; i += 1) {
    const box = editor.boxes[i];
    const rect = getBoxRectPixels(box, panelRect);
    const color = box.id === editor.selectedBoxId ? boxSelectedColor : boxColor;
    drawRectOutlinePixels(rect, EDITOR_LINE_WIDTH, color);
  }

  if (editor.startBoxId != null) {
    const startBox = getBoxById(editor.startBoxId);
    if (startBox) {
      const rect = getBoxRectPixels(startBox, panelRect);
      drawRectOutlinePixels(rect, EDITOR_LINE_WIDTH + 2, startColor);
    }
  }
}

export function loadEditorData() {
  return false;
}

export async function loadEditorDataFromFile(path = "assets/board.json") {
  try {
    const response = await fetch(path, { cache: "no-store" });
    if (!response.ok) {
      return false;
    }
    const parsed = await response.json();
    return applyEditorData(parsed);
  } catch (err) {
    return false;
  }
}

function applyEditorData(parsed) {
  if (!parsed || !Array.isArray(parsed.boxes) || !Array.isArray(parsed.edges)) {
    return false;
  }
  const startBoxId = Number(parsed.startBoxId);
  const boxes = [];
  let maxId = 0;
  for (const raw of parsed.boxes) {
    const box = sanitizeBox(raw);
    if (!box) {
      continue;
    }
    boxes.push(box);
    if (box.id > maxId) {
      maxId = box.id;
    }
  }
  const boxIds = new Set(boxes.map((box) => box.id));
  const edges = [];
  for (const raw of parsed.edges) {
    const edge = sanitizeEdge(raw, boxIds);
    if (!edge) {
      continue;
    }
    edges.push(edge);
  }
  editor.boxes = boxes;
  editor.edges = edges;
  editor.startBoxId = boxIds.has(startBoxId) ? startBoxId : null;
  editor.nextBoxId = maxId + 1;
  return true;
}

export function saveEditorData() {
  // No-op: client persistence disabled.
}

export async function exportEditorDataToFile(filename = "board.json") {
  const payload = buildEditorPayload();
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

export async function exportEditorDataToServer(endpoint = "/api/board") {
  const payload = buildEditorPayload();
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return response.ok;
  } catch (err) {
    return false;
  }
}

export function getEditorPayloadSignature() {
  return JSON.stringify(buildEditorPayload());
}

function buildEditorPayload() {
  return {
    version: 1,
    boxes: editor.boxes,
    edges: editor.edges,
    startBoxId: editor.startBoxId,
  };
}

export function setStartBoxId(nextId) {
  const startBox = nextId != null ? getBoxById(nextId) : null;
  editor.startBoxId = startBox ? startBox.id : null;
  moveState.active = false;
  moveState.team = null;
  moveState.pieces = [];
  moveState.path = [];
  moveState.stepIndex = 0;
  moveState.progress = 0;
  moveState.from = null;
  moveState.to = null;
  selectionState.active = false;
  selectionState.steps = 0;
  selectionState.groups = [];
  turnState.current = "player";
  turnState.awaitingMonster = false;
  turnState.monsterDelayRemaining = 0;
  turnState.bonusThrowCount = { player: 0, monster: 0 };
  turnState.freeThrowFor = null;
  gameState.playerTurnCount = 0;
  gameState.winner = null;
  gameState.goalCounts.player = 0;
  gameState.goalCounts.monster = 0;
  yutThrowState.used = 0;
  updateYutThrowUI();
  yutAnim.state = "idle";
  yutAnim.frames = null;
  yutAnim.frame = 0;
  yutAnim.elapsed = 0;
  yutAnim.resultSteps = 0;
  yutAnim.resultKey = null;
  yutAnim.moveQueued = false;
  yutResultOverlay.active = false;
  yutResultOverlay.elapsed = 0;
  yutResultOverlay.holdElapsed = 0;
  oneMoreOverlay.active = false;
  oneMoreOverlay.elapsed = 0;
  oneMoreOverlay.holdElapsed = 0;
  oneMoreOverlay.team = null;
  resetPiecesForStart();
  appState.requestDraw?.();
}

export function handleEditorPointerDown(event) {
  if (event.button === 2) {
    event.preventDefault();
    const panelRect = getPanelRectForInput();
    const point = getCanvasPoint(event);
    if (!pointInRect(point, panelRect)) {
      return;
    }
    const hitBox = findBoxAtPoint(point, panelRect);
    if (hitBox) {
      removeBox(hitBox.id);
    }
    return;
  }

  if (event.button !== 0) {
    return;
  }

  const panelRect = getPanelRectForInput();
  const point = getCanvasPoint(event);
  if (!pointInRect(point, panelRect)) {
    editor.selectedBoxId = null;
    editor.selectedEdgeId = null;
    appState.requestDraw?.();
    return;
  }

  const hitBox = findBoxAtPoint(point, panelRect);
  if (hitBox) {
    const now = performance.now();
    if (now - editor.lastClickTime < 300 && editor.lastClickBoxId === hitBox.id) {
      editor.dragMode = "move";
      editor.dragBoxId = hitBox.id;
      const center = getBoxCenterPixels(hitBox, panelRect);
      editor.dragOffset = { x: point.x - center.x, y: point.y - center.y };
      editor.pendingClick = null;
      editor.linkFromId = null;
      editor.linkPreviewPos = null;
      editor.selectedBoxId = hitBox.id;
      editor.selectedEdgeId = null;
      editor.lastClickTime = 0;
      editor.lastClickBoxId = null;
      canvas.setPointerCapture(event.pointerId);
      appState.requestDraw?.();
      return;
    }
    editor.pendingClick = { boxId: hitBox.id, start: point };
    editor.dragMode = null;
    editor.linkFromId = hitBox.id;
    editor.linkPreviewPos = { x: point.x, y: point.y };
    editor.selectedBoxId = hitBox.id;
    editor.selectedEdgeId = null;
    canvas.setPointerCapture(event.pointerId);
    appState.requestDraw?.();
    return;
  }

  const hitEdge = findEdgeAtPoint(point, panelRect);
  if (hitEdge != null) {
    editor.selectedEdgeId = hitEdge;
    editor.selectedBoxId = null;
    appState.requestDraw?.();
    return;
  }

  editor.selectedBoxId = null;
  editor.selectedEdgeId = null;
  appState.requestDraw?.();
}

export function handleEditorPointerMove(event) {
  const panelRect = getPanelRectForInput();
  const point = getCanvasPoint(event);

  if (editor.dragMode === "move" && editor.dragBoxId != null) {
    moveBoxToPoint(editor.dragBoxId, point, panelRect);
    return;
  }

  if (editor.pendingClick) {
    const dx = point.x - editor.pendingClick.start.x;
    const dy = point.y - editor.pendingClick.start.y;
    if (!editor.dragMode && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      editor.dragMode = "link";
    }
    if (editor.dragMode === "link") {
      editor.linkPreviewPos = { x: point.x, y: point.y };
      appState.requestDraw?.();
    }
  }
}

export function handleEditorPointerUp(event) {
  const panelRect = getPanelRectForInput();
  const point = getCanvasPoint(event);

  if (editor.dragMode === "move") {
    editor.dragMode = null;
    editor.dragBoxId = null;
    editor.dragOffset = { x: 0, y: 0 };
    saveEditorData();
    appState.requestDraw?.();
    if (canvas.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
    return;
  }

  if (editor.dragMode === "link") {
    const targetBox = findBoxAtPoint(point, panelRect);
    if (targetBox && targetBox.id !== editor.linkFromId) {
      addEdge(editor.linkFromId, targetBox.id);
    }
  }

  if (editor.pendingClick && editor.dragMode !== "link") {
    editor.lastClickTime = performance.now();
    editor.lastClickBoxId = editor.pendingClick.boxId;
  }

  editor.pendingClick = null;
  editor.dragMode = null;
  editor.linkFromId = null;
  editor.linkPreviewPos = null;
  appState.requestDraw?.();
  if (canvas.hasPointerCapture(event.pointerId)) {
    canvas.releasePointerCapture(event.pointerId);
  }
}

export function handleEditorKeyDown(event) {
  const key = event.key.toLowerCase();
  if (key === "s") {
    if (editor.selectedBoxId != null) {
      const nextStart = editor.startBoxId === editor.selectedBoxId ? null : editor.selectedBoxId;
      setStartBoxId(nextStart);
      saveEditorData();
    }
    return true;
  }
  if (event.key === "Delete" || event.key === "Backspace") {
    if (editor.selectedEdgeId != null) {
      removeEdge(editor.selectedEdgeId);
    } else if (editor.selectedBoxId != null) {
      removeBox(editor.selectedBoxId);
    }
    return true;
  }
  return false;
}

function sanitizeBox(raw) {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const id = Number(raw.id);
  const x = Number(raw.x);
  const y = Number(raw.y);
  const sizeRaw = Number(raw.size);
  const w = Number(raw.w);
  const h = Number(raw.h);
  if (!Number.isFinite(id) || !Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }
  let size = Number.isFinite(sizeRaw) ? sizeRaw : null;
  if (!Number.isFinite(size)) {
    if (Number.isFinite(w) && Number.isFinite(h)) {
      size = Math.min(w, h);
    } else if (Number.isFinite(w)) {
      size = w;
    } else if (Number.isFinite(h)) {
      size = h;
    }
  }
  if (!Number.isFinite(size)) {
    return null;
  }
  const safeSize = clamp(size, 0.02, 0.4);
  const safeX = clamp(x, 0, 1);
  const safeY = clamp(y, 0, 1);
  return { id, x: safeX, y: safeY, size: safeSize };
}

function sanitizeEdge(raw, boxIds) {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const from = Number(raw.from);
  const to = Number(raw.to);
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return null;
  }
  if (!boxIds.has(from) || !boxIds.has(to) || from === to) {
    return null;
  }
  return { from, to };
}

function findBoxAtPoint(point, panelRect) {
  for (let i = editor.boxes.length - 1; i >= 0; i -= 1) {
    const box = editor.boxes[i];
    const rect = getBoxRectPixels(box, panelRect);
    if (pointInRect(point, rect)) {
      return box;
    }
  }
  return null;
}

function findEdgeAtPoint(point, panelRect) {
  let closestIndex = null;
  let closestDistance = EDGE_HIT_DISTANCE;
  for (let i = 0; i < editor.edges.length; i += 1) {
    const edge = editor.edges[i];
    const fromBox = getBoxById(edge.from);
    const toBox = getBoxById(edge.to);
    if (!fromBox || !toBox) {
      continue;
    }
    const start = getBoxCenterPixels(fromBox, panelRect);
    const end = getBoxCenterPixels(toBox, panelRect);
    const distance = distanceToSegment(point, start, end);
    if (distance <= closestDistance) {
      closestDistance = distance;
      closestIndex = i;
    }
  }
  return closestIndex;
}

function distanceToSegment(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const t = clamp(
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy),
    0,
    1
  );
  const projX = start.x + t * dx;
  const projY = start.y + t * dy;
  return Math.hypot(point.x - projX, point.y - projY);
}

function createBoxAt(point, panelRect) {
  const size = DEFAULT_BOX_SIZE;
  const x = (point.x - panelRect.left) / panelRect.width;
  const y = (point.y - panelRect.bottom) / panelRect.height;
  const clamped = clampBoxCenterNormalized(x, y, size, panelRect);
  const box = {
    id: editor.nextBoxId++,
    x: clamped.x,
    y: clamped.y,
    size,
  };
  editor.boxes.push(box);
  editor.selectedBoxId = box.id;
  editor.selectedEdgeId = null;
  saveEditorData();
  appState.requestDraw?.();
}

function removeBox(id) {
  editor.boxes = editor.boxes.filter((box) => box.id !== id);
  editor.edges = editor.edges.filter((edge) => edge.from !== id && edge.to !== id);
  if (editor.selectedBoxId === id) {
    editor.selectedBoxId = null;
  }
  if (editor.startBoxId === id) {
    setStartBoxId(null);
  } else {
    resetPiecesForStart();
  }
  editor.selectedEdgeId = null;
  saveEditorData();
  appState.requestDraw?.();
}

function addEdge(from, to) {
  if (from === to) {
    return;
  }
  if (editor.edges.some((edge) => edge.from === from && edge.to === to)) {
    return;
  }
  editor.edges.push({ from, to });
  editor.selectedEdgeId = editor.edges.length - 1;
  editor.selectedBoxId = null;
  saveEditorData();
  appState.requestDraw?.();
}

function removeEdge(index) {
  if (index == null || index < 0 || index >= editor.edges.length) {
    return;
  }
  editor.edges.splice(index, 1);
  editor.selectedEdgeId = null;
  saveEditorData();
  appState.requestDraw?.();
}

function moveBoxToPoint(boxId, point, panelRect) {
  const box = getBoxById(boxId);
  if (!box) {
    return;
  }
  const centerX = point.x - editor.dragOffset.x;
  const centerY = point.y - editor.dragOffset.y;
  const x = (centerX - panelRect.left) / panelRect.width;
  const y = (centerY - panelRect.bottom) / panelRect.height;
  const clamped = clampBoxCenterNormalized(x, y, box.size, panelRect);
  box.x = clamped.x;
  box.y = clamped.y;
  appState.requestDraw?.();
}

function drawLinePixels(start, end, thickness, color) {
  const quad = lineToNdcQuad(start, end, thickness, canvas.width, canvas.height);
  if (!quad) {
    return;
  }
  drawQuad(quad, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), whiteTexture, color);
}

function drawArrowHeadPixels(start, end, length, width, color) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dist = Math.hypot(dx, dy);
  if (dist < length * 1.2) {
    return;
  }
  const ux = dx / dist;
  const uy = dy / dist;
  const nx = -uy;
  const ny = ux;

  const tip = end;
  const baseCenter = { x: end.x - ux * length, y: end.y - uy * length };
  const half = width * 0.5;
  const left = { x: baseCenter.x + nx * half, y: baseCenter.y + ny * half };
  const right = { x: baseCenter.x - nx * half, y: baseCenter.y - ny * half };

  drawTrianglePixels(tip, left, right, color);
}

function drawTrianglePixels(p0, p1, p2, color) {
  const positions = new Float32Array([
    (p0.x / canvas.width) * 2 - 1,
    (p0.y / canvas.height) * 2 - 1,
    (p1.x / canvas.width) * 2 - 1,
    (p1.y / canvas.height) * 2 - 1,
    (p2.x / canvas.width) * 2 - 1,
    (p2.y / canvas.height) * 2 - 1,
  ]);
  const texcoords = new Float32Array([0, 0, 1, 0, 0, 1]);
  drawTriangles(positions, texcoords, color);
}

function drawRectOutlinePixels(rect, thickness, color) {
  const left = rect.left;
  const right = rect.left + rect.width;
  const bottom = rect.bottom;
  const top = rect.bottom + rect.height;
  drawLinePixels({ x: left, y: bottom }, { x: right, y: bottom }, thickness, color);
  drawLinePixels({ x: right, y: bottom }, { x: right, y: top }, thickness, color);
  drawLinePixels({ x: right, y: top }, { x: left, y: top }, thickness, color);
  drawLinePixels({ x: left, y: top }, { x: left, y: bottom }, thickness, color);
}
